import { useEffect, useRef, useState } from 'react';
import 'moment/locale/pt-br';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import { setEtapaSessionCapitalFlow } from 'services/authentication/auth';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import { StatusCessaoEnum } from 'components/Shared/Enums/StatusCessaoEnum';
import { deleteRegistrosTemporarios, getListarHistoricoCessoes } from '../../../../api/capitalFlow';

const useGestaoCessaoHook = () => {
  const [rows, setRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [filtrosAtuais, setFiltrosAtuais] = useState({});
  const [loading, setLoading] = useState(false);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);
  const [dadosDetalhes, setDadosDetalhes] = useState([]);
  const [contadorFiltros, setContadorFiltros] = useState(0);
  const [selectedRowsMap, setSelectedRowsMap] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const navigate = useNavigate();
  const inputsRef = useRef();

  useEffect(() => {
    carregarTitulos(paginationModel, {});
  }, []);

  const carregarTitulos = (model = paginationModel, novosFiltros = null, newSortModel = sortModel) => {
    const filtrosBase = novosFiltros ?? filtrosAtuais;
    const filtrosLimpos = cleanFilters(filtrosBase);
    const listaAtual = rows;
    const sort = newSortModel?.[0];

    setRows([]);
    setFiltrosAtuais(filtrosLimpos);

    const { page, pageSize } = model;
    const params = {
      ...filtrosLimpos,
      data_solicitacao_de: formatDateUS(filtrosLimpos.data_solicitacao_de),
      data_solicitacao_ate: formatDateUS(filtrosLimpos.data_solicitacao_ate),
      page: page,
      per_page: pageSize,
      order_by: sort?.sort ?? null,
      column_filter: sort?.field ?? null
    };

    setLoading(true);
    getListarHistoricoCessoes(params)
      .then(response => {
        const { data } = response.data;
        setRows(data ?? []);
        setRowCount(response.data.meta.total);
      })
      .catch(() => {
        setRows(listaAtual);
        openSnackbarMain('Ocorreu um erro ao buscar as cessões', 'error');
      })
      .finally(() => setLoading(false));
  };

  const formatDateUS = value =>
    value ? moment(value, ['DD/MM/YYYY', moment.ISO_8601], true).format('YYYY-MM-DD') : null;

  const formataValor = valor =>
    parseFloat(valor).toLocaleString('pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const irParaJornada = () => {
    deleteRegistrosTemporarios();
    setEtapaSessionCapitalFlow('NovaCessao');
    navigate('/capital-flow/nova-cessao', {
      state: { buscarDados: false }
    });
  };

  const toolbarTabela = () => (
    <GridToolbarContainer className="tabela-toolbar">
      <div className="toolbar-spacer" />
      <GridToolbarQuickFilter className="search-grid" placeholder="Procurar..." debounceMs={500} />
    </GridToolbarContainer>
  );

  const parseISODateLocal = s => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
      return isNaN(d) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };

  const isBlank = v =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

  const cleanFilters = (obj = {}) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => !isBlank(v)));

  const verificaContaFiltros = filtros => {
    const on = v => (typeof v === 'string' ? v.trim() !== '' : !!v);
    let n = 0;
    if (on(filtros.numero_operacao_one7)) n++;
    if (on(filtros.status_cessao)) n++;
    if (on(filtros.numero_documento)) n++;
    if (on(filtros.data_solicitacao_de)) n++;
    if (on(filtros.data_solicitacao_ate)) n++;
    if (on(filtros.valor_de)) n++;
    if (on(filtros.valor_ate)) n++;
    return n;
  };

  const fmtBRDate = iso => {
    const d = parseISODateLocal(iso);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const columns = [
    {
      field: 'NUMEROBORDEROONE7',
      headerName: 'Nº Operação',
      flex: 1,
      minWidth: 110,
      valueFormatter: ({ value }) => `${value ? value : '- -'}`
    },
    { field: 'QTDETITULOS', headerName: 'Qtd. de títulos', flex: 1, minWidth: 110 },
    {
      field: 'DTHRINSERT',
      headerName: 'Data da solicitação',
      flex: 1.0,
      minWidth: 140,
      valueFormatter: ({ value }) => fmtBRDate(value)
    },
    {
      field: 'VALORTOTAL',
      headerName: 'Valor total',
      flex: 1.0,
      minWidth: 140,
      valueFormatter: ({ value }) => `R$ ${formataValor(value ?? 0)}`
    },
    {
      field: 'CODCESSOESCAPITALFLOWSTATUS',
      headerName: 'Status',
      flex: 1,
      minWidth: 100,
      renderCell: params => (
        <span className={`badge-cessao color-${getStatusKeyByValue(params.value)}`}>
          {params.row.DESCRICAOSTATUS}
        </span>
      )
    },
    {
      field: 'detalhes',
      headerName: '',
      flex: 0.8,
      sortable: false,
      minWidth: 120,
      renderCell: params => (
        <button className="btn-detalhes-grid" onClick={() => mostrarTelaDetalhes(params.row)}>
          <EditIcon /> Detalhes
        </button>
      )
    }
  ];

  const getStatusKeyByValue = value => {
    const n = Number(value);
    return (
      Object.keys(StatusCessaoEnum)
        .find(key => StatusCessaoEnum[key] === n)
        .toLowerCase() ?? ''
    );
  };

  const mostrarTelaDetalhes = row => {
    setMostrarDetalhes(true);
    setDadosDetalhes(row);
  };

  const rowSelectionModelFiltered = rows
    .map(row => String(row.id))
    .filter(id => selectedRowsMap[id] !== undefined);

  const handleRowSelectionModelChange = newRowSelectionModel => {
    const consistentIds = newRowSelectionModel.map(String);
    const currentPageRowIds = rows.map(row => String(row.id));
    const newSelectedRowsMap = { ...selectedRowsMap };

    for (const rowId of currentPageRowIds) {
      const isRowSelected = consistentIds.includes(rowId);
      const currentRow = rows.find(row => String(row.id) === rowId);

      if (isRowSelected) {
        newSelectedRowsMap[rowId] = currentRow;
      } else {
        delete newSelectedRowsMap[rowId];
      }
    }

    setSelectedRowsMap(newSelectedRowsMap);
  };

  const handleFiltrar = filtros => {
    const filtrosLimpos = cleanFilters(filtros);
    const newModel = {
      ...paginationModel,
      page: 0
    };

    setPaginationModel(newModel);
    setContadorFiltros(verificaContaFiltros(filtros));
    carregarTitulos(newModel, filtrosLimpos);
  };

  const handleSortModelChange = model => {
    setSortModel(model);
    carregarTitulos(paginationModel, filtrosAtuais, model);
  };

  const handlePaginationModelChange = model => {
    setPaginationModel(model);
    carregarTitulos(model, filtrosAtuais, sortModel);
  };

  const atualizarGridHistoricoCessao = () => {
    carregarTitulos(paginationModel, filtrosAtuais, sortModel);
  };

  return {
    handleRowSelectionModelChange,
    atualizarGridHistoricoCessao,
    handlePaginationModelChange,
    rowSelectionModelFiltered,
    handleSortModelChange,
    setMostrarDetalhes,
    setFiltrosAtuais,
    setDadosDetalhes,
    mostrarDetalhes,
    paginationModel,
    contadorFiltros,
    dadosDetalhes,
    toolbarTabela,
    filtrosAtuais,
    handleFiltrar,
    irParaJornada,
    inputsRef,
    sortModel,
    rowCount,
    columns,
    loading,
    rows
  };
};

export default useGestaoCessaoHook;
